import { StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import VisuallyHidden from '@reach/visually-hidden';
import React, { useContext } from 'react';
import { getCorrectCurrencySettings } from '../../../../helpers/utilsPricing';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import nanoid from 'nanoid';

interface Props {
  /**
   * Formatted Total Price.
   */
  formattedTotalPrice: string;
  /**
   * Formatted Total Price.
   */
  shortenedFormattedTotalPrice: string;
  /**
   * Currency Code
   */
  currencyCode: string;
  /**
   * Currency Code
   */
  currencySymbol: string;
  /**
   * Currency Styles
   */
  currencyStyles: $TSFixMe;
  /**
   * currencyCodeStyles props for using into withStyle.
   */
  currencyCodeStyles: $TSFixMe;
  /**
   * currencySymbolStyles props for using into withStyle.
   */
  currencySymbolStyles: $TSFixMe;
  /**
   * priceStyles props for using into withStyle.
   */
  priceStyles: $TSFixMe;
  /**
   * priceDisclaimerStyles props for using into withStyle.
   */
  priceDisclaimerStyles: $TSFixMe;
  /**
   * priceExtraInfo props for using into withStyle.
   */
  priceExtraInfo: $TSFixMe;
  /**
   * pricePromoStyles props for using into withStyle.
   */
  pricePromoStyles: $TSFixMe;
  /**
   * Show price promo.
   */
  showPricePromo: boolean;
  /**
   * Show currency code.
   */
  showCurrencyCode: boolean;
  /**
   * Show currency symbol.
   */
  showCurrencySymbol: boolean;
  /**
   * Show asterisk
   */
  showPriceDisclaimer: boolean;
  /**
   * Show price extra info
   */
  showPriceExtraInfo: boolean;
  /**
   * Show short price
   */
  showShortPrice: boolean;
  /**
   * Custom label key for pricePromo
   */
  pricePromoLabelKey: string;
  /**
   * Custom label key for pricePromo
   */
  priceExtraInfoLabelKey: string;
  /**
   * Define if show the currency symbol as a <sup> or <span> tag
   */
  showCurrencySymbolSub: boolean;
  /**
   * Currency value to display
   */
  currencyToDisplay: string;
  /**
   * Define in what order should be displayed the currency
   */
  currencyInFront: boolean;
  /**
   * currencyToDisplayStyles props for using into withStyle.
   */
  currencyToDisplayStyles: $TSFixMe;
  /**
   * Advanced configuration to currencySettings
   */
  advancedCurrencySettings: $TSFixMe;
  /**
   * Show label price disclaimerd menssage
   */
  showLabelPriceDisclaimerdMenssage?: boolean;
  id: string;
}

// currencyToDisplay atom just render wethever you pass: "Dog"
const renderCurrencyToDisplay = (
  currencyToDisplay: string,
  currencyToDisplayStyles: $TSFixMe,
  isInFront: boolean
) => (
  <Text
    paddingRight={
      isInFront && currencyToDisplay.charAt(currencyToDisplay.length - 1) !== ' ' ? '1' : '0'
    }
    paddingLeft={!isInFront && currencyToDisplay.charAt(0) !== ' ' ? '1' : '0'}
    {...currencyToDisplayStyles}
    style={{ whiteSpace: 'pre', ...currencyToDisplayStyles?.style }}
    floatLeft
  >
    {currencyToDisplay}
  </Text>
);

function FareItemPrice(props: StyledProps<Props>) {
  const {
    formattedTotalPrice,
    shortenedFormattedTotalPrice,
    currencyCode,
    currencySymbol,
    currencyCodeStyles,
    currencySymbolStyles,
    currencyStyles,
    priceStyles,
    priceDisclaimerStyles,
    priceExtraInfo,
    pricePromoStyles,
    showPricePromo,
    showCurrencyCode,
    showCurrencySymbol,
    showPriceDisclaimer,
    showPriceExtraInfo,
    pricePromoLabelKey,
    priceExtraInfoLabelKey,
    showCurrencySymbolSub,
    currencyToDisplayStyles,
    advancedCurrencySettings,
    currencyToDisplay,
    currencyInFront,
    showLabelPriceDisclaimerdMenssage,
    showShortPrice,
    id
  } = props;

  const tag = showCurrencySymbolSub ? 'sup' : 'span';
  const Tag = tag;
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const correctCurrencySettings = getCorrectCurrencySettings(
    {
      currencyToDisplay,
      currencyInFront
    },
    advancedCurrencySettings,
    currencyCode
  );

  const currency = (
    <>
      {showCurrencyCode && (
        <Text
          paddingRight={correctCurrencySettings.currencyInFront ? '1' : '0'}
          paddingLeft={!correctCurrencySettings.currencyInFront ? '1' : '0'}
          {...currencyCodeStyles}
          floatLeft
        >
          {currencyCode}
        </Text>
      )}
      {showCurrencySymbol && (
        <Text {...currencySymbolStyles} floatLeft>
          <Tag className={`fr-${tag}`}>{currencySymbol}</Tag>
        </Text>
      )}
    </>
  );

  const _id = nanoid();

  return (
    <Box className={`${className} fr-fare-item-price`} style={style} id={id ?? _id}>
      {showPricePromo && <Text {...pricePromoStyles}>{translate(pricePromoLabelKey, 'From')}</Text>}
      <Box className="fr-clearfix fr-fare-item-price-currency" {...currencyStyles} display="flex">
        {/* currencyToDisplay in Front */}
        {correctCurrencySettings.currencyToDisplay && correctCurrencySettings.currencyInFront
          ? renderCurrencyToDisplay(
              correctCurrencySettings.currencyToDisplay,
              currencyToDisplayStyles,
              correctCurrencySettings.currencyInFront
            )
          : correctCurrencySettings.currencyToDisplay === '' &&
            correctCurrencySettings.currencyInFront &&
            currency}
        <Text {...priceStyles} floatLeft>
          {showShortPrice ? shortenedFormattedTotalPrice : formattedTotalPrice}
        </Text>
        {/* currencyToDisplay in Back */}
        {correctCurrencySettings.currencyToDisplay && !correctCurrencySettings.currencyInFront
          ? renderCurrencyToDisplay(
              correctCurrencySettings.currencyToDisplay,
              currencyToDisplayStyles,
              correctCurrencySettings.currencyInFront
            )
          : correctCurrencySettings.currencyToDisplay === '' &&
            !correctCurrencySettings.currencyInFront &&
            currency}
        {showPriceDisclaimer && (
          <>
            <Text ariaHidden="true" {...priceDisclaimerStyles} floatLeft>
              *
            </Text>
            {showLabelPriceDisclaimerdMenssage && (
              <VisuallyHidden>
                {' '}
                {translate('cmp-price-disclaimer', 'Price subject to changes')}
              </VisuallyHidden>
            )}
          </>
        )}
      </Box>
      {showPriceExtraInfo && (
        <Text {...priceExtraInfo} floatLeft>
          {translate(priceExtraInfoLabelKey, 'Price extra info text')}
        </Text>
      )}
    </Box>
  );
}

FareItemPrice.defaultProps = {
  formattedTotalPrice: '',
  shortenedFormattedTotalPrice: '',
  showShortPrice: false,
  currencyCode: 'USD',
  currencySymbol: '$',
  currencyStyles: {},
  currencyCodeStyles: {},
  currencySymbolStyles: {},
  priceStyles: {},
  priceDisclaimerStyles: {},
  priceExtraInfo: {},
  pricePromoStyles: {},
  showPricePromo: true,
  showPriceExtraInfo: false,
  showCurrencyCode: true,
  showCurrencySymbol: true,
  showPriceDisclaimer: true,
  showCurrencySymbolSub: true,
  showLabelPriceDisclaimerdMenssage: true,
  pricePromoLabelKey: 'fare_promo_label',
  priceExtraInfoLabelKey: 'cmp-price-extra-info',
  currencyToDisplay: '',
  currencyInFront: true,
  currencyToDisplayStyles: {},
  advancedCurrencySettings: {}
};

FareItemPrice.componentName = 'FareItemPrice';

export default FareItemPrice;
